/**
 * Offline Storage Service
 * 
 * Implements IndexedDB storage using Dexie.js for offline-first data persistence.
 * Stores activities, workouts, courses, and pending actions for offline access.
 */

import Dexie, { type Table } from 'dexie';

/**
 * Activity data structure for offline storage
 */
export interface StoredActivity {
  id: number;
  name: string;
  sport_type: string;
  start_date: string;
  distance: number;
  moving_time: number;
  elapsed_time: number;
  total_elevation_gain: number;
  average_speed: number;
  max_speed: number;
  average_heartrate?: number;
  max_heartrate?: number;
  average_watts?: number;
  max_watts?: number;
  kilojoules?: number;
  tss?: number;
  intensity_factor?: number;
  normalized_power?: number;
  map?: {
    summary_polyline: string;
  };
  cached_at: number;
}

/**
 * Workout data structure for offline storage
 */
export interface StoredWorkout {
  id: number;
  athlete_id: number;
  course_id?: number;
  scheduled_date: string;
  status: 'pending' | 'completed' | 'skipped';
  priority: 'low' | 'medium' | 'high';
  notes?: string;
  completed_activity_id?: number;
  assigned_by_trainer_id?: number;
  structure?: unknown;
  cached_at: number;
}

/**
 * Course/Template data structure for offline storage
 */
export interface StoredCourse {
  id: number;
  name: string;
  description?: string;
  sport_type: string;
  structure: unknown;
  duration_seconds?: number;
  distance_meters?: number;
  created_by_trainer_id?: number;
  is_template: boolean;
  cached_at: number;
}

/**
 * Pending action data structure for offline storage
 */
export interface StoredPendingAction {
  id: string;
  type: string;
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  data?: unknown;
  timestamp: number;
  retryCount: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
}

/**
 * Dexie database class for offline storage
 */
export class OfflineDatabase extends Dexie {
  activities!: Table<StoredActivity, number>;
  workouts!: Table<StoredWorkout, number>;
  courses!: Table<StoredCourse, number>;
  pendingActions!: Table<StoredPendingAction, string>;

  constructor() {
    super('StravaAnalyticsDB');

    // Define database schema
    this.version(1).stores({
      activities: 'id, sport_type, start_date, cached_at',
      workouts: 'id, athlete_id, scheduled_date, status, cached_at',
      courses: 'id, sport_type, is_template, created_by_trainer_id, cached_at',
      pendingActions: 'id, type, status, timestamp',
    });
  }
}

/**
 * Database instance
 */
export const db = new OfflineDatabase();

/**
 * Cache expiration time in milliseconds (7 days)
 */
const CACHE_EXPIRATION_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Store activities in offline storage
 * 
 * @param activities - Array of activities to store
 * @returns Promise that resolves when activities are stored
 */
export async function cacheActivities(activities: Omit<StoredActivity, 'cached_at'>[]): Promise<void> {
  try {
    const activitiesWithTimestamp = activities.map(activity => ({
      ...activity,
      cached_at: Date.now(),
    }));

    await db.activities.bulkPut(activitiesWithTimestamp);
    console.log(`Cached ${activities.length} activities`);
  } catch (error) {
    console.error('Failed to cache activities:', error);
    throw error;
  }
}

/**
 * Get activities from offline storage
 * 
 * @param filters - Optional filters for activities
 * @returns Promise that resolves with array of activities
 */
export async function getCachedActivities(filters?: {
  sport_type?: string;
  start_date_from?: string;
  start_date_to?: string;
}): Promise<StoredActivity[]> {
  try {
    let collection = db.activities.toCollection();

    if (filters?.sport_type) {
      collection = db.activities.where('sport_type').equals(filters.sport_type);
    }

    let activities = await collection.toArray();

    // Apply date filters if provided
    if (filters?.start_date_from) {
      activities = activities.filter(a => a.start_date >= filters.start_date_from!);
    }
    if (filters?.start_date_to) {
      activities = activities.filter(a => a.start_date <= filters.start_date_to!);
    }

    // Sort by start date descending
    activities.sort((a, b) => b.start_date.localeCompare(a.start_date));

    return activities;
  } catch (error) {
    console.error('Failed to get cached activities:', error);
    return [];
  }
}

/**
 * Get a single activity from offline storage
 * 
 * @param activityId - The activity ID
 * @returns Promise that resolves with the activity or undefined
 */
export async function getCachedActivity(activityId: number): Promise<StoredActivity | undefined> {
  try {
    return await db.activities.get(activityId);
  } catch (error) {
    console.error('Failed to get cached activity:', error);
    return undefined;
  }
}

/**
 * Store workouts in offline storage
 * 
 * @param workouts - Array of workouts to store
 * @returns Promise that resolves when workouts are stored
 */
export async function cacheWorkouts(workouts: Omit<StoredWorkout, 'cached_at'>[]): Promise<void> {
  try {
    const workoutsWithTimestamp = workouts.map(workout => ({
      ...workout,
      cached_at: Date.now(),
    }));

    await db.workouts.bulkPut(workoutsWithTimestamp);
    console.log(`Cached ${workouts.length} workouts`);
  } catch (error) {
    console.error('Failed to cache workouts:', error);
    throw error;
  }
}

/**
 * Get workouts from offline storage
 * 
 * @param filters - Optional filters for workouts
 * @returns Promise that resolves with array of workouts
 */
export async function getCachedWorkouts(filters?: {
  athlete_id?: number;
  status?: 'pending' | 'completed' | 'skipped';
  scheduled_date_from?: string;
  scheduled_date_to?: string;
}): Promise<StoredWorkout[]> {
  try {
    let collection = db.workouts.toCollection();

    if (filters?.athlete_id) {
      collection = db.workouts.where('athlete_id').equals(filters.athlete_id);
    }

    let workouts = await collection.toArray();

    // Apply additional filters
    if (filters?.status) {
      workouts = workouts.filter(w => w.status === filters.status);
    }
    if (filters?.scheduled_date_from) {
      workouts = workouts.filter(w => w.scheduled_date >= filters.scheduled_date_from!);
    }
    if (filters?.scheduled_date_to) {
      workouts = workouts.filter(w => w.scheduled_date <= filters.scheduled_date_to!);
    }

    // Sort by scheduled date ascending
    workouts.sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));

    return workouts;
  } catch (error) {
    console.error('Failed to get cached workouts:', error);
    return [];
  }
}

/**
 * Get a single workout from offline storage
 * 
 * @param workoutId - The workout ID
 * @returns Promise that resolves with the workout or undefined
 */
export async function getCachedWorkout(workoutId: number): Promise<StoredWorkout | undefined> {
  try {
    return await db.workouts.get(workoutId);
  } catch (error) {
    console.error('Failed to get cached workout:', error);
    return undefined;
  }
}

/**
 * Store courses/templates in offline storage
 * 
 * @param courses - Array of courses to store
 * @returns Promise that resolves when courses are stored
 */
export async function cacheCourses(courses: Omit<StoredCourse, 'cached_at'>[]): Promise<void> {
  try {
    const coursesWithTimestamp = courses.map(course => ({
      ...course,
      cached_at: Date.now(),
    }));

    await db.courses.bulkPut(coursesWithTimestamp);
    console.log(`Cached ${courses.length} courses`);
  } catch (error) {
    console.error('Failed to cache courses:', error);
    throw error;
  }
}

/**
 * Get courses/templates from offline storage
 * 
 * @param filters - Optional filters for courses
 * @returns Promise that resolves with array of courses
 */
export async function getCachedCourses(filters?: {
  sport_type?: string;
  is_template?: boolean;
  created_by_trainer_id?: number;
}): Promise<StoredCourse[]> {
  try {
    let collection = db.courses.toCollection();

    if (filters?.is_template !== undefined) {
      collection = db.courses.where('is_template').equals(filters.is_template ? 1 : 0);
    }

    let courses = await collection.toArray();

    // Apply additional filters
    if (filters?.sport_type) {
      courses = courses.filter(c => c.sport_type === filters.sport_type);
    }
    if (filters?.created_by_trainer_id) {
      courses = courses.filter(c => c.created_by_trainer_id === filters.created_by_trainer_id);
    }

    return courses;
  } catch (error) {
    console.error('Failed to get cached courses:', error);
    return [];
  }
}

/**
 * Get a single course from offline storage
 * 
 * @param courseId - The course ID
 * @returns Promise that resolves with the course or undefined
 */
export async function getCachedCourse(courseId: number): Promise<StoredCourse | undefined> {
  try {
    return await db.courses.get(courseId);
  } catch (error) {
    console.error('Failed to get cached course:', error);
    return undefined;
  }
}

/**
 * Clear expired cache entries
 * 
 * @returns Promise that resolves when expired entries are cleared
 */
export async function clearExpiredCache(): Promise<void> {
  try {
    const expirationTime = Date.now() - CACHE_EXPIRATION_MS;

    const [activitiesDeleted, workoutsDeleted, coursesDeleted] = await Promise.all([
      db.activities.where('cached_at').below(expirationTime).delete(),
      db.workouts.where('cached_at').below(expirationTime).delete(),
      db.courses.where('cached_at').below(expirationTime).delete(),
    ]);

    console.log(
      `Cleared expired cache: ${activitiesDeleted} activities, ${workoutsDeleted} workouts, ${coursesDeleted} courses`
    );
  } catch (error) {
    console.error('Failed to clear expired cache:', error);
  }
}

/**
 * Clear all cached data
 * 
 * @returns Promise that resolves when all data is cleared
 */
export async function clearAllCache(): Promise<void> {
  try {
    await Promise.all([
      db.activities.clear(),
      db.workouts.clear(),
      db.courses.clear(),
    ]);

    console.log('All cached data cleared');
  } catch (error) {
    console.error('Failed to clear all cache:', error);
    throw error;
  }
}

/**
 * Get database statistics
 * 
 * @returns Promise that resolves with database statistics
 */
export async function getDatabaseStats(): Promise<{
  activities: number;
  workouts: number;
  courses: number;
  pendingActions: number;
}> {
  try {
    const [activities, workouts, courses, pendingActions] = await Promise.all([
      db.activities.count(),
      db.workouts.count(),
      db.courses.count(),
      db.pendingActions.count(),
    ]);

    return {
      activities,
      workouts,
      courses,
      pendingActions,
    };
  } catch (error) {
    console.error('Failed to get database stats:', error);
    return {
      activities: 0,
      workouts: 0,
      courses: 0,
      pendingActions: 0,
    };
  }
}

/**
 * Initialize the database and perform cleanup
 */
export async function initializeDatabase(): Promise<void> {
  try {
    // Open the database
    await db.open();
    console.log('Offline database initialized');

    // Clear expired cache entries
    await clearExpiredCache();
  } catch (error) {
    console.error('Failed to initialize database:', error);
    throw error;
  }
}

// Initialize database when the module is loaded
if (typeof window !== 'undefined') {
  initializeDatabase().catch(console.error);
}